import * as React from 'react';
import { VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import './selector-button.scss';
import { SelectorButtonOption } from './SelectorButtonOption';
import SelectorButtonProps from './SelectorButton.interface';

interface State {
  open: boolean;
}

const SelectorButton = ({
  id,
  arrowRenderer,
  disabled,
  name,
  onChange,
  onOpen,
  value,
  searchable,
  options,
  onSelect,
  onClose,
  selected,
}: SelectorButtonProps): JSX.Element => {
  const [open, setOpen] = React.useState(false);

  const displayDropdown = (): string => (open ? '' : 'displayNone');

  const onOpenSelect = (): void => {
    setOpen(true);
  };

  const onCloseSelect = (): void => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const select = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>|React.KeyboardEvent<HTMLButtonElement>): void => {
    const selectedValue: string = e.currentTarget.id;
    onCloseSelect();
    onSelect(selectedValue);
  };

  const onToggle = (): void => {
    if (open) {
      onCloseSelect();
    } else {
      onOpenSelect();
    }
  };

  const renderArrow = (): React.ReactNode => <VtxIcon className="selectorArrow" type="caret-down" fontSize={11} />;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const optionList = (): any => options.map(
    (option: SelectorButtonOption) => ((selected !== option.value && option.userSettable)
      ? (
        <li
          key={option.value}
          className={option.statusClass}
        >
          {/* eslint-disable-next-line react/button-has-type */}
          <button
            style={{ textAlign: 'left', textTransform: 'capitalize' }}
            onClick={select}
            onKeyDown={select}
            id={option.value}
          >
            {option.label.toLowerCase()}
          </button>
        </li>
      )
      : ''),
  );

  const selectedOptionClass = (): string => options.find(
    (option) => option.value === selected,
  ).bulletClass;

  const onClickHandler = (event: MouseEvent): void => {
    // Get the Selector Element (Parent)
    const dropDownButton: HTMLElement = document.getElementById('vtxSelectorButton');

    // Clicked Element
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let targetElement: any = event.target;

    // Check if one of the Selector or one of its Children was Clicked
    do {
      // Selector Element found
      if (targetElement === dropDownButton) {
        return;
      }

      // Go up the DOM
      targetElement = targetElement.parentNode;
    } while (targetElement);

    // Clicked was Outside the Selector, close the Fly Out Menu if Open
    setOpen(false);
  };

  React.useEffect(() => {
    // Add Window Listener for Click Events
    window.addEventListener('click', onClickHandler);

    return (): void => {
      // Remove Window Listener for Click Events
      window.removeEventListener('click', onClickHandler);
    };
  }, []);

  return (
    <nav id="vtxSelectorButton" className="vtxSelectorButton">
      <div
        className={`vtxSelectorButtonPill ${selectedOptionClass()}`}
        onClick={onToggle}
        onKeyDown={onToggle}
        role="button"
        tabIndex={0}
      >
        <span>{selected}</span>
        { renderArrow() }
      </div>
      <ul className={`vtxSelectorButtonList ${displayDropdown()}`}>
        { optionList() }
      </ul>
    </nav>
  );
};

export default SelectorButton;
